<template>
    <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-100 to-blue-50">

        <!-- Toggle Filter Button (top-left corner) -->
        <button @click="toggleFilter"
            class="fixed top-24 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-gray-100 h-12 w-12 flex flex-col justify-center items-center">
            <font-awesome-icon :icon="['fas', 'filter']" class="text-gray-700 text-xl" />
        </button>

        <!-- Sliding Sidebar Filter -->
        <transition name="slide">
            <aside v-show="showFilter"
                class="fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 flex flex-col border-r">
                <div class="p-6 border-b">
                    <h2 class="text-xl font-bold text-gray-800 w-full flex items-center justify-center pt-20 mt-1">
                        <font-awesome-icon :icon="['fas', 'sliders-h']" class="mr-2 text-blue-600" />
                        Filters
                    </h2>
                </div>
                <div class="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Entity</label>
                        <select v-model="filters.entity" class="w-full px-2 py-1 border outline-none">
                            <option value="ADPOLY">ADPOLY</option>
                            <option value="FCHS">FCHS</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Campus</label>
                        <div class="border px-4 py-2 h-auto flex items-center relative cursor-pointer"
                            :class="activeDropDown == 'campus' ? 'z-30' : ''">
                            <div class="flex flex-grow justify-between items-center"
                                @click.stop="showCampusDropDown = !showCampusDropDown">
                                <div class="text-sm mr-2 sm:mb-0 flex-shrink-0">All</div>
                                <icon-chevron-down
                                    class="h-4 w-4 transform transition-transform duration-300 ease-in-out"
                                    :class="{ 'rotate-180': showCampusDropDown }" />
                            </div>
                            <div v-show="showCampusDropDown"
                                class="flex flex-col h-40 overflow-y-auto shadow-lg px-2 pt-2 absolute w-full bg-white border left-0 top-10">
                                <div v-for="(item, index) in filteredCampuses" :key="index"
                                    class="flex items-center mb-2">
                                    <input type="checkbox" :value="item.campus_code" :id="`academic-year-${index}`"
                                        v-model="filters.campus_code" class="mr-2" />
                                    <label :for="`academic-year-${index}`" class="text-sm">{{ item.campus_code
                                    }}</label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Local or Non Local</label>
                        <select v-model="filters.local_or_non_local" class="w-full px-2 py-1 border outline-none">
                            <option value="">All</option>
                            <option value="Local">Local</option>
                            <option value="Non Local">Non Local</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Gender</label>
                        <select v-model="filters.gender" class="w-full px-2 py-1 border outline-none">
                            <option value="">All</option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Academic Year</label>
                        <div class="border px-4 py-2 h-auto flex items-center relative cursor-pointer"
                            :class="activeDropDown == 'academic_year' ? 'z-30' : ''">
                            <div class="flex flex-grow justify-between items-center"
                                @click.stop="showAcademicYearDropDown = !showAcademicYearDropDown">
                                <div class="text-sm mr-2 sm:mb-0 flex-shrink-0">All</div>
                                <icon-chevron-down
                                    class="h-4 w-4 transform transition-transform duration-300 ease-in-out"
                                    :class="{ 'rotate-180': showAcademicYearDropDown }" />
                            </div>
                            <div v-show="showAcademicYearDropDown"
                                class="flex flex-col h-40 overflow-y-auto shadow-lg px-2 pt-2 absolute w-full bg-white border left-0 top-10">
                                <div v-for="(item, index) in filteredAcademicYears" :key="index"
                                    class="flex items-center mb-2">
                                    <input type="checkbox" :value="item.academic_year" :id="`academic-year-${index}`"
                                        v-model="filters.academic_year" class="mr-2" />
                                    <label :for="`academic-year-${index}`" class="text-sm">{{ item.academic_year
                                    }}</label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Employment Status</label>
                        <multiselect v-model="filters.employment_status" :options="employmentStatusOptions"
                            :multiple="true" :close-on-select="false" placeholder="All" track-by="value" label="label">
                        </multiselect>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Got Job</label>
                        <select v-model="filters.got_job_within" class="w-full px-2 py-1 border outline-none">
                            <option value="">All</option>
                            <option>Before graduation</option>
                            <option>Within 3 months</option>
                            <option>Within 6 months</option>
                            <option>Within 9 months</option>
                            <option>Within 12 months</option>
                            <option>After 1 year</option>
                        </select>
                    </div>
                </div>

                <!-- Apply Button -->
                <div class="p-6 flex justify-center space-x-6 border-t">
                    <button @click.stop="clearFilters"
                        class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold">
                        Clear
                    </button>
                    <button @click.stop="applyFilters"
                        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold">
                        Apply
                    </button>
                </div>
            </aside>
        </transition>

        <div class="container mx-auto px-4 sm:px-8 py-6 space-y-8">
            <div class="flex justify-between items-center flex-wrap gap-4">
                <h1 class="text-2xl font-bold text-gray-800 flex items-center">
                    <font-awesome-icon :icon="['fas', 'chart-line']" class="text-blue-600 mr-2" />
                    <div class="uppercase">Employment Dashboard ({{ filters.entity }})</div>
                </h1>
                <div class="flex items-center flex-wrap gap-4">
                    <div v-if="timeStamp" class="flex items-center space-x-2 text-sm text-gray-500 mt-2">
                        <font-awesome-icon :icon="['fas', 'clock']" class="text-gray-400 w-4 h-4" />
                        <span class="font-medium text-gray-700">Last Updated:</span>
                        <span>{{ timeStamp }}</span>
                    </div>
                    <div @click.stop="downloadReport" title="Download Report"
                        class="flex-shrink-0 cursor-pointer p-2 rounded-md bg-primary-blue-bg text-primary-blue-text hover:bg-primary-blue-bg-dark hover:text-primary-blue-text-dark transition-colors duration-200 shadow-sm">
                        <font-awesome-icon :icon="['fas', 'download']" class="w-5 h-5" />
                    </div>
                    <ExportAll :filters="exportFilters" />
                    <button v-if="hasPermission('employment-bulk-upload')" type="button" title="Bulk Upload Employment"
                        class="flex-shrink-0 cursor-pointer p-2 rounded-md bg-primary-blue-bg text-primary-blue-text hover:bg-primary-blue-bg-dark hover:text-primary-blue-text-dark transition-colors duration-200 shadow-sm"
                        @click="showBulkUploadModal = true">
                        <font-awesome-icon :icon="['fas', 'upload']" class="w-5 h-5" />
                    </button>
                </div>
            </div>

            <!-- Chart Grid -->
            <div ref="screenshotArea" class="w-full flex flex-col space-y-8">
                <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    <!-- Bar Chart -->
                    <div class="bg-white p-6 shadow-md">
                        <h2 class="text-lg font-semibold text-gray-700 mb-2">Alumni employed by entity</h2>
                        <apexchart type="bar" height="250" :options="barChartOptions" :series="barChartSeries"
                            @dataPointSelection="onEntityChartSelect" />
                    </div>

                    <!-- Radial Progress -->
                    <div class="bg-white p-6 shadow-md">
                        <h2 class="text-lg font-semibold text-gray-700 mb-2">
                            <span class="header-info-trigger inline-flex items-center gap-1"
                                @mouseenter="showRateTooltip($event, overallEmploymentRateTooltip)"
                                @mouseleave="hideRateTooltip">
                                <span>Overall employment rate</span>
                                <span class="header-info-icon" aria-hidden="true">i</span>
                            </span>
                        </h2>
                        <apexchart type="radialBar" height="300" :options="radialChartOptions"
                            :series="radialChartSeries" />
                    </div>

                    <!-- Pie Chart -->
                    <div class="bg-white p-6 shadow-md">
                        <h2 class="text-lg font-semibold text-gray-700 mb-2">Employment status</h2>
                        <apexchart type="pie" height="300" :options="pieChartOptions" :series="pieChartSeries"
                            @dataPointSelection="onEmploymentStatusChartSelect" />
                    </div>

                    <div class="flex flex-col">
                        <div class="ht-389 relative w-full perspective"
                            aria-label="Toggle Graduates/Employed By Gender">
                            <div class="relative w-full h-full transition-transform duration-700 transform-style-preserve-3d"
                                :class="{ 'rotate-y-180': flipped }">
                                <!-- Front Side -->
                                <div
                                    class="absolute w-full h-full bg-white p-6 shadow-md backface-hidden flex flex-col">
                                    <h2 class="text-lg font-semibold text-gray-700 mb-2">Graduates by gender</h2>
                                    <div @click.stop>
                                        <apexchart type="donut" :options="graduatedDonutChartOptions"
                                            :series="graduatedDonutChartSeries" height="300"
                                            @dataPointSelection="onGraduatesByGenderChartSelect" />
                                    </div>
                                    <div class="mt-auto text-center text-sm text-gray-500 cursor-pointer"
                                        @click.stop="toggleFlip">Click to see Employed</div>
                                </div>
                                <!-- Back Side -->
                                <div
                                    class="absolute w-full h-full bg-white p-6 shadow-md backface-hidden rotate-y-180 flex flex-col">
                                    <h2 class="text-lg font-semibold text-gray-700 mb-2">Employed by gender</h2>
                                    <div @click.stop>
                                        <apexchart type="donut" :options="employedDonutChartOptions"
                                            :series="employedDonutChartSeries" height="300"
                                            @dataPointSelection="onEmployedByGenderChartSelect" />
                                    </div>
                                    <div class="mt-auto text-center text-sm text-gray-500 cursor-pointer"
                                        @click.stop="toggleFlip">Click to see Graduates</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="bg-white p-6 shadow-md">
                        <h2 class="text-lg font-semibold text-gray-700 mb-2">Employment by campus</h2>
                        <apexchart v-if="graduatedVsEmployedPerCampusChartSeries.length"
                            :key="'employment-by-campus-' + graduatedVsEmployedPerCampusChartKey" type="bar"
                            :height="graduatedVsEmployedPerCampusChartHeight"
                            :options="graduatedVsEmployedPerCampusChartOptions"
                            :series="graduatedVsEmployedPerCampusChartSeries" @dataPointSelection="onCampusChartSelect">
                        </apexchart>
                        <div v-else class="text-center py-16 text-gray-500">
                            <p class="text-sm text-gray-600">No campus data yet</p>
                        </div>
                    </div>

                    <div class="bg-white p-6 shadow-md">
                        <h2 class="text-lg font-semibold text-gray-700 mb-2">Job related to major</h2>
                        <apexchart type="pie" height="300" :options="jobRelatedToMajorChartOptions"
                            :series="jobRelatedToMajorChartSeries"
                            @dataPointSelection="onJobRelatedToMajorChartSelect" />
                    </div>

                    <div class="bg-white p-6 shadow-md">
                        <h2 class="text-lg font-semibold text-gray-700 mb-2">Employment source</h2>
                        <apexchart type="pie" height="300" :options="employmentSourceChartOptions"
                            :series="employmentSourceChartSeries" @dataPointSelection="onEmploymentSourceChartSelect" />
                    </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div v-for="(card, index) in eploymentRatePerAcademicYearForCardsData" :key="index"
                        class="bg-gradient-to-br from-white via-gray-50 to-white shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-200 p-6">
                        <h2 class="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b">
                            {{ card.title }}
                        </h2>

                        <div class="space-y-3 text-sm text-gray-700">
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">🎓 Graduated</span>
                                <span class="font-bold text-gray-800 text-base">{{ card.graduated }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">✅ Eligible for Employment</span>
                                <span class="font-bold text-gray-800 text-base">{{ card.eligible_employment }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">💼 Employed</span>
                                <span class="font-bold text-gray-800 text-base">{{ card.employed }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">🚫 Unemployed</span>
                                <span class="font-bold text-gray-800 text-base">{{ card.unemployed }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">🚫 Unemployed(&lt; 9 months)</span>
                                <span class="font-bold text-gray-800 text-base">{{ card.not_counted }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">📞 Not Contacted</span>
                                <span class="font-bold text-gray-800 text-base">{{ card.not_contacted }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">📈 Employment Rate</span>
                                <span class="rate-badge rounded px-2 py-1 text-xs font-semibold"
                                    :class="getRateColor(card.employment_rate)"
                                    @mouseenter="showRateTooltip($event, buildEmploymentRateTooltip(card))"
                                    @mouseleave="hideRateTooltip">
                                    {{ card.employment_rate }}%
                                </span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">📊 Adjusted Employment Rate</span>
                                <span class="rate-badge rounded px-2 py-1 text-xs font-semibold"
                                    :class="getRateColor(card.adjustable_employment_rate)"
                                    @mouseenter="showRateTooltip($event, buildAdjustedEmploymentRateTooltip(card))"
                                    @mouseleave="hideRateTooltip">
                                    {{ card.adjustable_employment_rate }}%
                                </span>
                            </div>

                            <div class="pt-3">
                                <h3 class="font-semibold text-gray-800 mb-1">📅 Employment Rate Within</h3>
                                <div class="space-y-2 text-sm">
                                    <div v-for="(item, rateIndex) in getUniqueWithinMonthRates(card)" :key="rateIndex"
                                        class="flex justify-between items-center">
                                        <span class="text-gray-600">{{ item.label }}</span>
                                        <span class="rate-badge rounded px-2 py-1 text-xs font-semibold"
                                            :class="getRateColor(item.rate)"
                                            @mouseenter="showRateTooltip($event, buildWithinMonthRateTooltip(card, item))"
                                            @mouseleave="hideRateTooltip">
                                            {{ item.rate }}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="academicCardsData" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div v-for="(card, index) in academicCardsData" :key="index"
                        class="bg-gradient-to-br from-white via-gray-50 to-white shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-200 p-6">
                        <h2 class="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b">
                            {{ card.title }}
                        </h2>

                        <div class="space-y-3 text-sm text-gray-700">
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">🎓 Graduated</span>
                                <span class="font-bold text-gray-800 text-base">{{ card.graduated }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">✅ Eligible for Employment</span>
                                <span class="font-bold text-gray-800 text-base">{{ card.eligible_employment }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">💼 Employed</span>
                                <span class="font-bold text-gray-800 text-base">{{ card.employed }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">🚫 Unemployed</span>
                                <span class="font-bold text-gray-800 text-base">{{ card.unemployed }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">🚫 Unemployed(&lt; 9 months)</span>
                                <span class="font-bold text-gray-800 text-base">{{ card.not_counted }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">📞 Not Contacted</span>
                                <span class="font-bold text-gray-800 text-base">{{ card.not_contacted }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">📈 Employment Rate</span>
                                <span class="rate-badge rounded px-2 py-1 text-xs font-semibold"
                                    :class="getRateColor(card.employment_rate)"
                                    @mouseenter="showRateTooltip($event, buildEmploymentRateTooltip(card))"
                                    @mouseleave="hideRateTooltip">
                                    {{ card.employment_rate }}%
                                </span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-600">📊 Adjusted Employment Rate</span>
                                <span class="rate-badge rounded px-2 py-1 text-xs font-semibold"
                                    :class="getRateColor(card.adjustable_employment_rate)"
                                    @mouseenter="showRateTooltip($event, buildAdjustedEmploymentRateTooltip(card))"
                                    @mouseleave="hideRateTooltip">
                                    {{ card.adjustable_employment_rate }}%
                                </span>
                            </div>

                            <div class="pt-3">
                                <h3 class="font-semibold text-gray-800 mb-1">📅 Employment Rate Within</h3>
                                <div class="space-y-2 text-sm">
                                    <div v-for="(item, rateIndex) in getUniqueWithinMonthRates(card)" :key="rateIndex"
                                        class="flex justify-between items-center">
                                        <span class="text-gray-600">{{ item.label }}</span>
                                        <span class="rate-badge rounded px-2 py-1 text-xs font-semibold"
                                            :class="getRateColor(item.rate)"
                                            @mouseenter="showRateTooltip($event, buildWithinMonthRateTooltip(card, item))"
                                            @mouseleave="hideRateTooltip">
                                            {{ item.rate }}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="rateTooltip.visible" class="rate-tooltip-fixed"
                    :style="{ top: rateTooltip.top + 'px', left: rateTooltip.left + 'px' }">
                    <div class="rate-tooltip__title">{{ rateTooltip.title }}</div>
                    <div class="rate-tooltip__formula">{{ rateTooltip.formula }}</div>
                    <div class="rate-tooltip__line rate-tooltip__line--primary">
                        {{ rateTooltip.primaryLabel }}: <strong>{{ rateTooltip.primaryValue }}</strong>
                    </div>
                    <div class="rate-tooltip__line rate-tooltip__line--muted">
                        {{ rateTooltip.secondaryLabel }}: <strong>{{ rateTooltip.secondaryValue }}</strong>
                    </div>
                    <div class="rate-tooltip__divider">
                        {{ rateTooltip.totalLabel }}: <strong>{{ rateTooltip.totalValue }}</strong>
                    </div>
                    <div class="rate-tooltip__rate">
                        Rate: <strong>{{ rateTooltip.rate }}%</strong>
                        ({{ rateTooltip.primaryValue }} ÷ {{ rateTooltip.totalValue }})
                    </div>
                </div>

                <div class="bg-white p-6 shadow w-full">
                    <h2 class="text-xl font-semibold text-gray-700 mb-4">Employment rate over academic years</h2>
                    <apexchart type="line" height="400" :options="employmentRatePerAcademicYearChartOptions"
                        :series="employmentRatePerAcademicYearSeries"
                        @dataPointSelection="onEmploymentRateYearChartSelect" />
                </div>

                <div class="overflow-x-auto bg-white shadow-md border border-gray-100">
                    <table class="min-w-full divide-y divide-gray-200 text-xxs sm:text-sm">
                        <thead class="bg-gray-50 text-gray-600 font-semibold text-left">
                            <tr>
                                <th class="px-2 sm:px-4 py-1 sm:py-3 text-center">📅 Academic Year</th>
                                <th class="px-2 sm:px-4 py-1 sm:py-3 text-center">👨‍💼 Employed</th>
                                <th class="px-2 sm:px-4 py-1 sm:py-3 text-center">🚫 Unemployed</th>
                                <th class="px-2 sm:px-4 py-1 sm:py-3 text-center">🎓 Total Graduates</th>
                                <th class="px-2 sm:px-4 py-1 sm:py-3 text-center">✅ Eligible for Employment</th>
                                <th class="px-2 sm:px-4 py-1 sm:py-3 text-center">Employment Rate</th>
                                <th class="px-2 sm:px-4 py-1 sm:py-3 text-center">Adjustable Employment Rate</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-100 text-gray-700">
                            <tr v-for="(item, index) in employmentRatePerAcademicYear" :key="index"
                                class="hover:bg-gray-50 transition">
                                <td class="px-1 sm:px-3 py-1 sm:py-2 font-medium text-gray-800 text-center">{{
                                    item.academic_year }}</td>
                                <td class="px-1 sm:px-3 py-1 sm:py-2 text-green-700 font-semibold text-center">{{
                                    item.employed }}</td>
                                <td class="px-1 sm:px-3 py-1 sm:py-2 text-red-600 font-semibold text-center">{{
                                    item.unemployed }}</td>
                                <td class="px-1 sm:px-3 py-1 sm:py-2 text-blue-700 font-semibold text-center">{{
                                    item.graduated }}</td>
                                <td class="px-1 sm:px-3 py-1 sm:py-2 text-indigo-700 font-semibold text-center">{{
                                    item.eligible_employment }}</td>
                                <td class="px-1 sm:px-3 py-1 sm:py-2 font-semibold text-center">
                                    <span class="rate-badge rounded px-2 py-1 text-xs font-semibold"
                                        :class="getRateColor(item.employment_rate)"
                                        @mouseenter="showRateTooltip($event, buildEmploymentRateTooltip(item))"
                                        @mouseleave="hideRateTooltip">
                                        {{ item.employment_rate }}%
                                    </span>
                                </td>
                                <td class="px-1 sm:px-3 py-1 sm:py-2 font-semibold text-center">
                                    <span class="rate-badge rounded px-2 py-1 text-xs font-semibold"
                                        :class="getRateColor(item.adjustable_employment_rate)"
                                        @mouseenter="showRateTooltip($event, buildAdjustedEmploymentRateTooltip(item))"
                                        @mouseleave="hideRateTooltip">
                                        {{ item.adjustable_employment_rate }}%
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="bg-white p-6 shadow w-full">
                    <h2 class="text-xl font-semibold text-gray-700 mb-4">Employment by program</h2>
                    <apexchart v-if="employmentRatePerProgramSeries.length"
                        :key="'employment-by-program-' + employmentRatePerProgramChartKey" type="bar"
                        :height="employmentRatePerProgramChartHeight" :options="employmentRatePerProgramOptions"
                        :series="employmentRatePerProgramSeries" @dataPointSelection="onProgramChartSelect" />
                    <div v-else class="text-center py-16 text-gray-500">
                        <p class="text-sm text-gray-600">No program data yet</p>
                    </div>
                </div>

                <div class="overflow-x-auto bg-white shadow-md border border-gray-100">
                    <table class="min-w-full divide-y divide-gray-200 text-xxs sm:text-sm">
                        <thead class="bg-gray-50 text-gray-600 font-semibold text-left">
                            <tr>
                                <th class="px-2 sm:px-4 py-1 sm:py-3 text-center">📘 Program</th>
                                <th class="px-2 sm:px-4 py-1 sm:py-3 text-center">👨‍💼 Employed</th>
                                <th class="px-2 sm:px-4 py-1 sm:py-3 text-center">🚫 Unemployed</th>
                                <th class="px-2 sm:px-4 py-1 sm:py-3 text-center">🎓 Total Graduates</th>
                                <th class="px-2 sm:px-4 py-1 sm:py-3 text-center">✅ Eligible for Employment</th>
                                <th class="px-2 sm:px-4 py-1 sm:py-3 text-center">Employment Rate</th>
                                <th class="px-2 sm:px-4 py-1 sm:py-3 text-center">Adjustable Employment Rate</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-100 text-gray-700">
                            <tr v-for="(item, index) in employmentRatePerProgram" :key="index"
                                class="hover:bg-gray-50 transition">
                                <td class="px-1 sm:px-3 py-1 sm:py-2 font-medium text-gray-800">{{ item.specialization
                                }}
                                </td>
                                <td class="px-1 sm:px-3 py-1 sm:py-2 text-green-700 font-semibold text-center">{{
                                    item.employed }}</td>
                                <td class="px-1 sm:px-3 py-1 sm:py-2 text-red-600 font-semibold text-center">{{
                                    item.unemployed }}</td>
                                <td class="px-1 sm:px-3 py-1 sm:py-2 text-blue-700 font-semibold text-center">{{
                                    item.graduated }}</td>
                                <td class="px-1 sm:px-3 py-1 sm:py-2 text-indigo-700 font-semibold text-center">{{
                                    item.eligible_employment }}</td>
                                <td class="px-1 sm:px-3 py-1 sm:py-2 font-semibold text-center">
                                    <span class="rate-badge rounded px-2 py-1 text-xs font-semibold"
                                        :class="getRateColor(item.employment_rate)"
                                        @mouseenter="showRateTooltip($event, buildEmploymentRateTooltip(item))"
                                        @mouseleave="hideRateTooltip">
                                        {{ item.employment_rate }}%
                                    </span>
                                </td>
                                <td class="px-1 sm:px-3 py-1 sm:py-2 font-semibold text-center">
                                    <span class="rate-badge rounded px-2 py-1 text-xs font-semibold"
                                        :class="getRateColor(item.adjustable_employment_rate)"
                                        @mouseenter="showRateTooltip($event, buildAdjustedEmploymentRateTooltip(item))"
                                        @mouseleave="hideRateTooltip">
                                        {{ item.adjustable_employment_rate }}%
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div class="bg-white p-6 shadow w-full">
                        <h2 class="text-xl font-semibold text-gray-700 mb-4">Got job within</h2>
                        <apexchart type="bar" height="400" :options="gotJobWithinChartOptions"
                            :series="gotJobWithinChartSeries" @dataPointSelection="onGotJobWithinChartSelect">
                        </apexchart>
                    </div>

                    <div class="bg-white p-6 shadow w-full">
                        <h2 class="text-xl font-semibold text-gray-700 mb-4">Unemployed Reason</h2>
                        <apexchart type="bar" height="400" :options="unemployedReasonChartOption"
                            :series="unemployedReasonChartSeries" @dataPointSelection="onUnemployedReasonChartSelect">
                        </apexchart>
                    </div>

                    <EmployedByEmployer class="lg:col-span-2" :data="top10EmployersData"
                        :other-employers-data="otherEmployersData" @employer-click="onTopEmployerClick" />

                </div>
            </div>

        </div>
        <div class="h-full flex justify-center items-center" v-if="isLoading">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>
        <EmploymentDrilldownModal :visible="drilldown.visible" :title="drilldown.title" :file-name="drilldown.fileName"
            :base-filters="employmentDrilldownBaseFilters" :overrides="drilldown.overrides"
            @close="closeEmploymentDrilldown" />
        <EmploymentBulkUploadModal :visible="showBulkUploadModal" @close="showBulkUploadModal = false"
            @uploaded="onBulkUploadComplete" />
    </div>
</template>

<script>
import gql from 'graphql-tag';
import IconChevronDown from "../../components/icons/IconChevronDown.vue";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
import { formattedDateWithTimeAmPm } from "../../helpers/utils";
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import EmployedByEmployer from "./charts/EmployedByEmployer.vue";
import ExportAll from './excel/ExportAll.vue';
import EmploymentDrilldownModal from "./components/EmploymentDrilldownModal.vue";
import EmploymentBulkUploadModal from "./components/EmploymentBulkUploadModal.vue";

const EMPLOYMENT_DASHBOARD_QUERY = gql`
  query EmploymentDashboardReport($entity: String, $campus_code: [String], $local_or_non_local: String, $gender: String, $academic_year: [String], $got_job_within: String, $employment_status: [String]) {
    employmentReport(entity: $entity, campus_code: $campus_code, local_or_non_local: $local_or_non_local, gender: $gender, academic_year: $academic_year, got_job_within: $got_job_within, employment_status: $employment_status) {
      error
      error_code
      message
      data {
        placedByEntity {
            entity
            count
            graduated
        }
        employmentStatus {
            employment_status
            count
        }
        employmentRate {
            rate
            employed
            unemployed
        }
        eploymentRatePerAcademicYearForCards {
            title
            graduated
            eligible_employment
            employed
            unemployed
            not_counted
            not_contacted
            employed_eligible
            employment_rate
            adjustable_employment_rate
            within_3_months_cumulative
            within_6_months_cumulative
            within_9_months_cumulative
            within_12_months_cumulative
            within_3_months_adjustable_employment_rate
            within_6_months_adjustable_employment_rate
            within_9_months_adjustable_employment_rate
            within_12_months_adjustable_employment_rate
        }
        academicCards {
            title
            graduated
            eligible_employment
            employed
            unemployed
            not_counted
            not_contacted
            employed_eligible
            employment_rate
            adjustable_employment_rate
            within_3_months_cumulative
            within_6_months_cumulative
            within_9_months_cumulative
            within_12_months_cumulative
            within_3_months_adjustable_employment_rate
            within_6_months_adjustable_employment_rate
            within_9_months_adjustable_employment_rate
            within_12_months_adjustable_employment_rate
        }
        employmentRatePerAcademicYear {
            academic_year
            graduated
            employed
            unemployed
            eligible_employment
            employed_eligible
            employment_rate
            adjustable_employment_rate
        }
        employmentRatePerProgram {
            specialization
            graduated
            employed
            unemployed
            eligible_employment
            employed_eligible
            employment_rate
            adjustable_employment_rate
        }
        graduatedVsEmployedPerGender {
            gender
            graduated
            employed
        }
        graduatedVsEmployedPerCampus {
            campus_code
            graduated
            employed
        }
        gotJobWithinData {
            got_job_within
            count
        }
        unemployedReasonData {
            unemployed_reason
            count
        }
        jobRelatedToMajor {
            label
            count
        }
        top10Employers {
            employer
            count
        }
        otherEmployers {
            employer
            count
        }
        employmentSourceData {
            label
            code
            count
        }
      }
    }
  }
`;

const powerBIColors = [
    '#4472C4', // Blue
    '#ED7D31', // Orange
    '#A5A5A5', // Gray
    '#FFC000', // Yellow
    '#5B9BD5', // Light Blue
    '#70AD47', // Green
    '#264478'  // Dark Blue
];

export default {
    name: "employment-dashboard-index",
    components: {
        IconChevronDown,
        Multiselect,
        EmployedByEmployer,
        ExportAll,
        EmploymentDrilldownModal,
        EmploymentBulkUploadModal,
    },
    data() {
        return {
            isLoading: false,
            flipped: true,
            showFilter: false,
            filters: {
                entity: "ADPOLY",
                campus_code: [],
                local_or_non_local: "",
                gender: "",
                academic_year: [],
                got_job_within: "",
                employment_status: [],
            },
            employmentStatusOptions: [
                { value: "Employed", label: "Employed" },
                { value: "Unemployed", label: "Unemployed" },
                { value: "Not Contacted", label: "Not Contacted" },
            ],
            activeDropDown: "",
            academicYearsList: [],
            showAcademicYearDropDown: false,
            campusList: [],
            showCampusDropDown: false,
            barChartOptions: {
                chart: { toolbar: { show: false } },
                xaxis: { categories: [] },
                colors: ["#2F5597", "#70AD47"],
                plotOptions: {
                    bar: { columnWidth: "60%" }
                },
                dataLabels: {
                    enabled: true
                },
                legend: {
                    position: "top"
                },
            },
            barChartSeries: [
                {
                    name: "Total Graduates",
                    data: []
                },
                {
                    name: "Employed",
                    data: []
                }
            ],
            pieChartOptions: {
                labels: [],
                colors: ["#2F855A", "#C53030", "#ED8936"],
                legend: {
                    position: "bottom",
                    formatter: (seriesName, opts) => {
                        const seriesIndex = opts.seriesIndex;
                        const value = this.pieChartSeries[seriesIndex];
                        return `${seriesName} - ${value}`;
                    },
                },
            },
            pieChartSeries: [],
            radialChartOptions: {
                plotOptions: {
                    radialBar: {
                        hollow: {
                            size: "60%"
                        },
                        dataLabels: {
                            show: true,
                            name: {
                                show: false
                            },
                            value: {
                                show: true,
                                fontSize: "22px",
                                fontWeight: 600,
                                color: "#111827",
                                offsetY: 10,
                            }
                        }
                    }
                },
                labels: ["Employment Rate"],
                colors: ["#70AD47"]
            },
            radialChartSeries: [],
            overallEmploymentRateData: {
                rate: 0,
                employed: 0,
                unemployed: 0,
            },
            lineChartOptions: {
                chart: { toolbar: { show: false } },
                xaxis: {
                    categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
                },
                stroke: { curve: "smooth" },
                colors: ["#3B82F6"],
            },
            lineChartSeries: [
                {
                    name: "Applications",
                    data: [120, 150, 90, 130, 170, 200],
                }
            ],
            employmentRatePerAcademicYear: [],
            employmentRatePerAcademicYearChartOptions: {
                chart: {
                    toolbar: { show: false },
                    zoom: { enabled: false }
                },
                xaxis: {
                    categories: [],
                    title: { text: "Academic Year" }
                },
                yaxis: {
                    min: 0,
                    max: 100,
                    title: { text: "Employment Rate (%)" },
                    labels: {
                        formatter: val => `${val.toFixed(0)}%`
                    }
                },
                stroke: {
                    curve: "smooth",
                    width: 2
                },
                markers: {
                    size: 4
                },
                tooltip: {
                    y: {
                        formatter: val => `${val.toFixed(2)}%`
                    }
                },
                colors: ["#1F77B4", "#2CA02C"],
                legend: {
                    position: "top"
                }
            },
            employmentRatePerAcademicYearSeries: [
                {
                    name: "Employment Rate",
                    data: []
                },
                {
                    name: "Adjusted Employment Rate",
                    data: []
                }
            ],
            employmentRatePerProgram: [],
            employmentRatePerProgramChartHeight: 400,
            employmentRatePerProgramChartKey: 0,
            employmentRatePerProgramOptions: {
                chart: {
                    type: "bar",
                    stacked: true,
                    stackType: "normal",
                    toolbar: { show: false },
                    animations: {
                        enabled: false,
                    },
                },
                colors: ["#4472C4", "#E5E7EB"],
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: "58%",
                        borderRadius: 2,
                    },
                },
                stroke: {
                    width: 1,
                    colors: ["#ffffff"],
                },
                dataLabels: {
                    enabled: true,
                },
                xaxis: {
                    categories: [],
                },
                legend: {
                    position: "bottom",
                    fontSize: "13px",
                },
            },
            employmentRatePerProgramSeries: [],
            graduatedVsEmployedPerGender: [],
            graduatedDonutChartOptions: {
                chart: {
                    type: "donut"
                },
                labels: [],
                colors: [],
                legend: {
                    position: "bottom",
                    formatter: (seriesName, opts) => {
                        const seriesIndex = opts.seriesIndex;
                        const value = this.graduatedDonutChartSeries[seriesIndex];
                        return `${seriesName} - ${value}`;
                    },
                },
                dataLabels: {
                    enabled: true
                },
            },
            graduatedDonutChartSeries: [],
            employedDonutChartOptions: {
                chart: {
                    type: "donut"
                },
                labels: [],
                colors: [],
                legend: {
                    position: "bottom",
                    formatter: (seriesName, opts) => {
                        const seriesIndex = opts.seriesIndex;
                        const value = this.employedDonutChartSeries[seriesIndex];
                        return `${seriesName} - ${value}`;
                    },
                },
                dataLabels: {
                    enabled: true
                },
            },
            employedDonutChartSeries: [],
            graduatedVsEmployedPerCampus: [],
            graduatedVsEmployedPerCampusChartSeries: [],
            graduatedVsEmployedPerCampusChartHeight: 280,
            graduatedVsEmployedPerCampusChartKey: 0,
            graduatedVsEmployedPerCampusChartOptions: {
                chart: {
                    type: "bar",
                    stacked: true,
                    stackType: "normal",
                    toolbar: { show: false },
                    animations: {
                        enabled: false,
                    },
                },
                colors: ["#4472C4", "#E5E7EB"],
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: "58%",
                        borderRadius: 2,
                    },
                },
                stroke: {
                    width: 1,
                    colors: ["#ffffff"],
                },
                dataLabels: {
                    enabled: true,
                },
                xaxis: {
                    categories: [],
                },
                legend: {
                    position: "bottom",
                    fontSize: "13px",
                },
            },
            gotJobWithinData: [],
            gotJobWithinChartSeries: [],
            gotJobWithinChartOptions: {
                chart: {
                    type: 'bar',
                    height: 400
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        distributed: true,
                    }
                },
                colors: [],
                dataLabels: {
                    enabled: true
                },
                xaxis: {
                    categories: [],
                },
                legend: {
                    show: false,
                },
            },
            unemployedReasonData: [],
            unemployedReasonChartSeries: [],
            unemployedReasonChartOption: {
                chart: {
                    type: 'bar',
                    height: 400
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        distributed: true,
                    }
                },
                colors: [],
                dataLabels: {
                    enabled: true
                },
                xaxis: {
                    categories: [],
                },
                legend: {
                    show: false,
                },
            },
            jobRelatedToMajorChartOptions: {
                labels: [],
                colors: ["#2F855A", "#C53030", "#ED8936"],
                legend: {
                    position: "bottom",
                    formatter: (seriesName, opts) => {
                        const seriesIndex = opts.seriesIndex;
                        const value = this.jobRelatedToMajorChartSeries[seriesIndex];
                        return `${seriesName} - ${value}`;
                    },
                },
            },
            jobRelatedToMajorChartSeries: [],
            eploymentRatePerAcademicYearForCardsData: [],
            top10EmployersData: [],
            otherEmployersData: [],
            academicCardsData: [],
            rateTooltip: {
                visible: false,
                top: 0,
                left: 0,
                title: "",
                formula: "",
                primaryLabel: "",
                primaryValue: 0,
                secondaryLabel: "",
                secondaryValue: 0,
                totalLabel: "",
                totalValue: 0,
                rate: "0.0",
            },
            employmentSourceData: [],
            employmentSourceChartOptions: {
                labels: [],
                colors: [],
                legend: {
                    position: "bottom",
                    formatter: (seriesName, opts) => {
                        const seriesIndex = opts.seriesIndex;
                        const value = this.employmentSourceChartSeries[seriesIndex];
                        return `${seriesName} - ${value}`;
                    },
                },
            },
            employmentSourceChartSeries: [],
            employmentStatusLabels: [],
            drilldown: {
                visible: false,
                title: "",
                fileName: "Employment_Drilldown",
                overrides: {},
            },
            showBulkUploadModal: false,
        };
    },
    created() {
        this.getCampusList();
        this.getAcademicYears();
    },
    mounted() {
        this.loadChartData();
    },
    watch: {
        showAcademicYearDropDown(newVal) {
            if (newVal) {
                this.activeDropDown = 'academic_year';
                this.collapseDropDown('academic_year');
            }
        },
        showCampusDropDown(newVal) {
            if (newVal) {
                this.activeDropDown = 'campus';
                this.collapseDropDown('campus');
            }
        },
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        filteredCampuses() {
            if (this.filters.entity) {
                return this.campusList.filter(
                    (campus) => campus.entity === this.filters.entity
                );
            } else {
                return this.campusList;
            }
        },
        filteredAcademicYears() {
            if (this.filters.entity) {
                return this.academicYearsList.filter(
                    (academic_year) => academic_year.entity === this.filters.entity
                );
            } else {
                return this.academicYearsList;
            }
        },
        timeStamp() {
            const settings = this.$store.getters["settings/settings"] || [];
            const lastUpdated = settings.find(setting => setting.name === "last_updated");
            if (lastUpdated && lastUpdated.value) {
                return formattedDateWithTimeAmPm(lastUpdated.value);
            }
            return "";
        },
        overallEmploymentRateTooltip() {
            var employed = this.overallEmploymentRateData && this.overallEmploymentRateData.employed
                ? this.overallEmploymentRateData.employed
                : 0;
            var unemployed = this.overallEmploymentRateData && this.overallEmploymentRateData.unemployed
                ? this.overallEmploymentRateData.unemployed
                : 0;
            var total = employed + unemployed;
            return {
                title: "Overall Employment Rate",
                formula: "Employed ÷ (Employed + Unemployed) × 100",
                primaryLabel: "Employed",
                primaryValue: employed,
                secondaryLabel: "Unemployed",
                secondaryValue: unemployed,
                totalLabel: "Employed + Unemployed",
                totalValue: total,
                rate: this.formatTooltipRate(employed, total),
            };
        },
        exportFilters() {
            const f = { ...this.filters };
            if (f.employment_status && Array.isArray(f.employment_status)) {
                f.employment_status = f.employment_status.map(function (item) {
                    return typeof item === 'string' ? item : (item && item.value ? item.value : item);
                });
            }
            return f;
        },
        employmentDrilldownBaseFilters() {
            var f = this.filters || {};
            var base = {
                student_status: "IG",
            };
            if (f.entity) {
                base.entity = f.entity;
            }
            if (f.campus_code && f.campus_code.length) {
                base.campuses = f.campus_code;
            }
            if (f.local_or_non_local) {
                base.local_or_non_local = f.local_or_non_local;
            }
            if (f.gender) {
                base.gender = f.gender;
            }
            if (f.academic_year && f.academic_year.length) {
                base.academic_years = f.academic_year;
            }
            var gotJobList = this.mapGotJobWithinToList(f.got_job_within);
            if (gotJobList && gotJobList.length) {
                base.got_job_within_list = gotJobList;
            }
            var statusLabels = this.getEmploymentStatusFilterValues();
            if (statusLabels && statusLabels.length) {
                var values = [];
                var i;
                for (i = 0; i < statusLabels.length; i++) {
                    var mapped = this.mapEmploymentStatusLabelToValues(statusLabels[i]);
                    if (mapped && mapped.length) {
                        var j;
                        for (j = 0; j < mapped.length; j++) {
                            values.push(mapped[j]);
                        }
                    }
                }
                if (values.length) {
                    base.is_employed_values = values;
                }
            }
            return base;
        },
    },
    methods: {
        hasPermission(crudName) {
            if (this.role == 'superadmin') {
                return true;
            } else {
                const permissions = this.$store.getters.permissions;
                const permissionsArray = permissions.split(",");
                if (permissionsArray.includes(crudName)) {
                    return true;
                } else {
                    return false;
                }
            }
        },
        toggleFlip() {
            this.flipped = !this.flipped;
        },
        toggleFilter() {
            this.showFilter = !this.showFilter;
        },
        clearFilters() {
            this.toggleFilter();
            this.filters = {
                entity: "ADPOLY",
                campus_code: [],
                local_or_non_local: "",
                gender: "",
                academic_year: [],
                got_job_within: "",
                employment_status: [],
            };
            this.collapseDropDown();
            this.loadChartData();
        },
        applyFilters() {
            this.toggleFilter();
            this.loadChartData();
        },
        getEmploymentStatusFilterValues() {
            const selected = this.filters.employment_status;
            if (!selected || selected.length === 0) {
                return [];
            }
            return selected.map(function (item) {
                return typeof item === 'string' ? item : (item && item.value ? item.value : item);
            });
        },
        onBulkUploadComplete(payload) {
            this.loadChartData();

            var lastUpdated = payload && payload.lastUpdated ? payload.lastUpdated : "";
            if (!lastUpdated) {
                return;
            }

            var settings = this.$store.getters["settings/settings"] || [];
            var found = false;
            var updatedSettings = [];

            for (var i = 0; i < settings.length; i++) {
                var setting = settings[i];
                if (setting && setting.name === "last_updated") {
                    updatedSettings.push({
                        name: "last_updated",
                        value: lastUpdated,
                    });
                    found = true;
                } else if (setting) {
                    updatedSettings.push(setting);
                }
            }

            if (!found) {
                updatedSettings.push({
                    name: "last_updated",
                    value: lastUpdated,
                });
            }

            this.$store.commit("settings/setSettings", updatedSettings);
        },
        async loadChartData() {
            this.isLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: EMPLOYMENT_DASHBOARD_QUERY,
                    variables: {
                        entity: this.filters.entity,
                        campus_code: this.filters.campus_code,
                        local_or_non_local: this.filters.local_or_non_local,
                        gender: this.filters.gender,
                        academic_year: this.filters.academic_year,
                        got_job_within: this.filters.got_job_within,
                        employment_status: this.getEmploymentStatusFilterValues(),
                    },
                    fetchPolicy: 'network-only'
                });

                const data = response.data.employmentReport.data;

                this.barChartOptions = {
                    ...this.barChartOptions,
                    xaxis: {
                        ...this.barChartOptions.xaxis,
                        categories: data.placedByEntity.map(e => e.entity)
                    }
                };
                this.barChartSeries[0].data = data.placedByEntity.map(e => e.graduated);
                this.barChartSeries[1].data = data.placedByEntity.map(e => e.count);

                const labels = data.employmentStatus.map(e => e.employment_status);
                const values = data.employmentStatus.map(e => e.count);
                const pieColors = ["#2F855A", "#C53030", "#ED8936"];

                this.employmentStatusLabels = labels;
                this.pieChartOptions = {
                    ...this.pieChartOptions,
                    labels: labels,
                    colors: pieColors,
                    tooltip: this.buildPieDistributionTooltip(labels, values, pieColors),
                };

                this.pieChartSeries = values;

                this.overallEmploymentRateData = data.employmentRate || {
                    rate: 0,
                    employed: 0,
                    unemployed: 0,
                };
                this.radialChartSeries = [this.overallEmploymentRateData.rate];

                this.eploymentRatePerAcademicYearForCardsData = data.eploymentRatePerAcademicYearForCards;
                this.academicCardsData = data.academicCards;
                this.employmentRatePerAcademicYear = data.employmentRatePerAcademicYear || [];
                const years = this.employmentRatePerAcademicYear.map(e => e.academic_year);
                const rate = this.employmentRatePerAcademicYear.map(e => e.employment_rate);
                const adjusted = this.employmentRatePerAcademicYear.map(e => e.adjustable_employment_rate);
                this.employmentRatePerAcademicYearChartOptions = {
                    ...this.employmentRatePerAcademicYearChartOptions,
                    xaxis: {
                        ...this.employmentRatePerAcademicYearChartOptions.xaxis,
                        categories: years
                    },
                    tooltip: this.buildEmploymentRatePerAcademicYearTooltip(
                        this.employmentRatePerAcademicYear
                    ),
                };
                this.employmentRatePerAcademicYearSeries = [
                    {
                        name: "Employment Rate",
                        data: rate
                    },
                    {
                        name: "Adjusted Employment Rate",
                        data: adjusted
                    }
                ];


                this.employmentRatePerProgram = data.employmentRatePerProgram || [];
                var programChartHeight = this.getProgramEmploymentChartHeight(
                    this.employmentRatePerProgram
                );
                var programChartSeries = this.buildProgramEmploymentChartSeries(
                    this.employmentRatePerProgram
                );
                var programChartOptions = this.buildProgramEmploymentChartOptions(
                    this.employmentRatePerProgram
                );
                this.employmentRatePerProgramSeries = [];
                this.$nextTick(function () {
                    this.employmentRatePerProgramChartHeight = programChartHeight;
                    this.employmentRatePerProgramOptions = programChartOptions;
                    this.employmentRatePerProgramSeries = programChartSeries;
                    this.employmentRatePerProgramChartKey = this.employmentRatePerProgramChartKey + 1;
                }.bind(this));

                this.graduatedVsEmployedPerGender = data.graduatedVsEmployedPerGender;
                const graduatedByGenderLabels = this.graduatedVsEmployedPerGender.map(item => item.gender);
                const graduatedByGenderValues = this.graduatedVsEmployedPerGender.map(item => item.graduated);
                const graduatedByGenderColors = this.graduatedVsEmployedPerGender.map(item =>
                    item.gender === 'Male' ? '#4C78A8' : '#F58518'
                );
                this.graduatedDonutChartOptions = {
                    ...this.graduatedDonutChartOptions,
                    labels: graduatedByGenderLabels,
                    colors: graduatedByGenderColors,
                    tooltip: this.buildPieDistributionTooltip(
                        graduatedByGenderLabels,
                        graduatedByGenderValues,
                        graduatedByGenderColors
                    ),
                };
                this.graduatedDonutChartSeries = graduatedByGenderValues;
                const employedByGenderLabels = this.graduatedVsEmployedPerGender.map(item => item.gender);
                const employedByGenderValues = this.graduatedVsEmployedPerGender.map(item => item.employed);
                const employedByGenderColors = this.graduatedVsEmployedPerGender.map(item =>
                    item.gender === 'Male' ? '#4C78A8' : '#F58518'
                );
                this.employedDonutChartOptions = {
                    ...this.employedDonutChartOptions,
                    labels: employedByGenderLabels,
                    colors: employedByGenderColors,
                    tooltip: this.buildPieDistributionTooltip(
                        employedByGenderLabels,
                        employedByGenderValues,
                        employedByGenderColors
                    ),
                };
                this.employedDonutChartSeries = employedByGenderValues;

                this.graduatedVsEmployedPerCampus = data.graduatedVsEmployedPerCampus || [];
                var campusChartHeight = this.getCampusEmploymentChartHeight(
                    this.graduatedVsEmployedPerCampus
                );
                var campusChartSeries = this.buildCampusEmploymentChartSeries(
                    this.graduatedVsEmployedPerCampus
                );
                var campusChartOptions = this.buildCampusEmploymentChartOptions(
                    this.graduatedVsEmployedPerCampus
                );
                // Destroy chart first, then remount with new props to avoid vue3-apexcharts resetSeries race
                this.graduatedVsEmployedPerCampusChartSeries = [];
                this.$nextTick(function () {
                    this.graduatedVsEmployedPerCampusChartHeight = campusChartHeight;
                    this.graduatedVsEmployedPerCampusChartOptions = campusChartOptions;
                    this.graduatedVsEmployedPerCampusChartSeries = campusChartSeries;
                    this.graduatedVsEmployedPerCampusChartKey = this.graduatedVsEmployedPerCampusChartKey + 1;
                }.bind(this));

                this.gotJobWithinData = data.gotJobWithinData;
                const gotJobWithinDataCategories = this.gotJobWithinData.map(item => item.got_job_within);
                const gotJobWithinDataCount = this.gotJobWithinData.map(item => item.count);
                this.gotJobWithinChartOptions = {
                    ...this.gotJobWithinChartOptions,
                    xaxis: {
                        ...this.gotJobWithinChartOptions.xaxis,
                        categories: gotJobWithinDataCategories,
                    },
                    colors: gotJobWithinDataCategories.map((_, index) => powerBIColors[index % powerBIColors.length])
                }
                this.gotJobWithinChartSeries = [
                    {
                        name: 'Employed',
                        data: gotJobWithinDataCount
                    }
                ];

                this.unemployedReasonData = data.unemployedReasonData;
                const unemployedReasonDataCategories = this.unemployedReasonData.map(item => item.unemployed_reason);
                const unemployedReasonDataCount = this.unemployedReasonData.map(item => item.count);
                this.unemployedReasonChartOption = {
                    ...this.unemployedReasonChartOption,
                    xaxis: {
                        ...this.unemployedReasonChartOption.xaxis,
                        categories: unemployedReasonDataCategories,
                    },
                    colors: unemployedReasonDataCategories.map((_, index) => powerBIColors[index % powerBIColors.length])
                }
                this.unemployedReasonChartSeries = [
                    {
                        name: 'Unemployed',
                        data: unemployedReasonDataCount
                    }
                ];

                const jobRelatedToMajorLabels = data.jobRelatedToMajor.map(e => e.label);
                const jobRelatedToMajorValues = data.jobRelatedToMajor.map(e => e.count);
                const jobRelatedToMajorColors = ["#2F855A", "#C53030", "#ED8936"];
                this.jobRelatedToMajorChartOptions = {
                    ...this.jobRelatedToMajorChartOptions,
                    labels: jobRelatedToMajorLabels,
                    colors: jobRelatedToMajorColors,
                    tooltip: this.buildPieDistributionTooltip(
                        jobRelatedToMajorLabels,
                        jobRelatedToMajorValues,
                        jobRelatedToMajorColors
                    ),
                };
                this.jobRelatedToMajorChartSeries = jobRelatedToMajorValues;

                this.top10EmployersData = data.top10Employers;
                this.otherEmployersData = data.otherEmployers;

                this.employmentSourceData = data.employmentSourceData;
                const employmentSourceLabels = this.employmentSourceData.map(e => e.label);
                const employmentSourceValues = this.employmentSourceData.map(e => e.count);
                const employmentSourceColors = employmentSourceLabels.map((_, index) => powerBIColors[index % powerBIColors.length]);
                this.employmentSourceChartOptions = {
                    ...this.employmentSourceChartOptions,
                    labels: employmentSourceLabels,
                    colors: employmentSourceColors,
                    tooltip: this.buildPieDistributionTooltip(
                        employmentSourceLabels,
                        employmentSourceValues,
                        employmentSourceColors
                    ),
                };
                this.employmentSourceChartSeries = employmentSourceValues;

            } catch (error) {
                console.error("Failed to load employment dashboard:", error);
            }
            this.isLoading = false;
        },
        getRateColor(rate) {
            if (rate >= 90) return 'bg-green-100 text-green-700';
            if (rate >= 75) return 'bg-yellow-100 text-yellow-700';
            if (rate >= 50) return 'bg-orange-100 text-orange-700';
            return 'bg-red-100 text-red-700';
        },
        formatTooltipRate(part, total) {
            var safePart = part || 0;
            var safeTotal = total || 0;
            if (!safeTotal) {
                return "0.00";
            }
            return ((safePart / safeTotal) * 100).toFixed(2);
        },
        buildEmploymentRatePerAcademicYearTooltip(rows) {
            var self = this;
            return {
                shared: true,
                intersect: false,
                custom: function (opts) {
                    var dataPointIndex =
                        opts && opts.dataPointIndex !== undefined && opts.dataPointIndex !== null
                            ? opts.dataPointIndex
                            : null;
                    if (dataPointIndex === null || !rows || !rows[dataPointIndex]) {
                        return "";
                    }
                    var row = rows[dataPointIndex];
                    var year = row.academic_year || "";
                    var employed = row.employed || 0;
                    var graduated = row.graduated || 0;
                    var employedEligible = row.employed_eligible || 0;
                    var eligible = row.eligible_employment || 0;
                    var employmentRate = self.formatTooltipRate(employed, graduated);
                    var adjustedRate = self.formatTooltipRate(employedEligible, eligible);
                    return (
                        '<div style="padding:10px 12px;font-size:12px;line-height:1.45;text-align:left;pointer-events:none;background:#ffffff;color:#111827;min-width:220px;">' +
                        '<div style="font-weight:700;color:#111827;margin-bottom:8px;">' +
                        year +
                        "</div>" +
                        '<div style="font-weight:700;color:#1F77B4;margin-bottom:4px;">Employment Rate</div>' +
                        '<div style="color:#475569;font-size:11px;margin-bottom:4px;">Employed ÷ Graduated × 100</div>' +
                        '<div style="color:#144685;">Employed: <strong>' +
                        employed +
                        "</strong></div>" +
                        '<div style="color:#64748b;">Not Employed: <strong>' +
                        (graduated - employed) +
                        "</strong></div>" +
                        '<div style="margin-top:4px;padding-top:4px;border-top:1px solid #E5E7EB;color:#111827;">' +
                        "Graduated: <strong>" +
                        graduated +
                        "</strong></div>" +
                        '<div style="color:#111827;margin-bottom:10px;">Rate: <strong>' +
                        employmentRate +
                        "%</strong> (" +
                        employed +
                        " ÷ " +
                        graduated +
                        ")</div>" +
                        '<div style="font-weight:700;color:#2CA02C;margin-bottom:4px;">Adjusted Employment Rate</div>' +
                        '<div style="color:#475569;font-size:11px;margin-bottom:4px;">Employed (Eligible) ÷ Eligible for Employment × 100</div>' +
                        '<div style="color:#144685;">Employed (Eligible): <strong>' +
                        employedEligible +
                        "</strong></div>" +
                        '<div style="color:#64748b;">Eligible Not Employed: <strong>' +
                        (eligible - employedEligible) +
                        "</strong></div>" +
                        '<div style="margin-top:4px;padding-top:4px;border-top:1px solid #E5E7EB;color:#111827;">' +
                        "Eligible for Employment: <strong>" +
                        eligible +
                        "</strong></div>" +
                        '<div style="color:#111827;">Rate: <strong>' +
                        adjustedRate +
                        "%</strong> (" +
                        employedEligible +
                        " ÷ " +
                        eligible +
                        ")</div>" +
                        "</div>"
                    );
                },
            };
        },
        formatPieDistributionRate(part, total) {
            var safePart = part || 0;
            var safeTotal = total || 0;
            if (!safeTotal) {
                return "0.0";
            }
            return ((safePart / safeTotal) * 100).toFixed(1);
        },
        getCampusEmploymentTotal(row) {
            if (!row) {
                return 0;
            }
            return row.graduated || 0;
        },
        getCampusEmploymentNotEmployed(row) {
            if (!row) {
                return 0;
            }
            var total = row.graduated || 0;
            var employed = row.employed || 0;
            var remaining = total - employed;
            if (remaining < 0) {
                return 0;
            }
            return remaining;
        },
        getCampusEmploymentChartMaxY(rows) {
            var max = 0;
            var i;
            if (!rows || !rows.length) {
                return 10;
            }
            for (i = 0; i < rows.length; i++) {
                var total = this.getCampusEmploymentTotal(rows[i]);
                if (total > max) {
                    max = total;
                }
            }
            if (max <= 0) {
                return 10;
            }
            return Math.ceil(max * 1.15);
        },
        getCampusEmploymentChartHeight(rows) {
            var count = rows && rows.length ? rows.length : 0;
            if (count <= 2) {
                return 250;
            }
            if (count <= 4) {
                return 280;
            }
            if (count <= 6) {
                return 320;
            }
            return 360;
        },
        getCampusEmploymentChartColors() {
            if (this.filters && this.filters.entity === "FCHS") {
                return ["#CA8A04", "#E5E7EB"];
            }
            if (this.filters && this.filters.entity === "ADPOLY") {
                return ["#3e3934", "#E5E7EB"];
            }
            return ["#4472C4", "#E5E7EB"];
        },
        buildCampusEmploymentChartSeries(rows) {
            if (!rows || !rows.length) {
                return [];
            }
            var self = this;
            return [
                {
                    name: "Employed",
                    data: rows.map(function (result) {
                        return result.employed || 0;
                    }),
                },
                {
                    name: "Not Employed",
                    data: rows.map(function (result) {
                        return self.getCampusEmploymentNotEmployed(result);
                    }),
                },
            ];
        },
        getProgramEmploymentChartHeight(rows) {
            var count = rows && rows.length ? rows.length : 0;
            if (count <= 2) {
                return 250;
            }
            if (count <= 4) {
                return 300;
            }
            if (count <= 6) {
                return 360;
            }
            if (count <= 10) {
                return 420;
            }
            return Math.min(700, 280 + count * 28);
        },
        buildProgramEmploymentChartSeries(rows) {
            return this.buildCampusEmploymentChartSeries(rows);
        },
        buildProgramEmploymentChartOptions(rows) {
            var categories = (rows || []).map(function (result) {
                return result.specialization || "";
            });
            var barHeight = "58%";
            if (rows && rows.length <= 2) {
                barHeight = "48%";
            } else if (rows && rows.length <= 4) {
                barHeight = "42%";
            } else if (rows && rows.length <= 6) {
                barHeight = "50%";
            }
            var colors = this.getCampusEmploymentChartColors();
            var self = this;
            return {
                chart: {
                    type: "bar",
                    stacked: true,
                    stackType: "normal",
                    toolbar: { show: false },
                    animations: {
                        enabled: false,
                    },
                },
                colors: colors,
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: barHeight,
                        borderRadius: 2,
                        dataLabels: {
                            total: {
                                enabled: true,
                                offsetX: 8,
                                style: {
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    color: "#1F2937",
                                },
                                formatter: function (val, opts) {
                                    var dataPointIndex = opts && opts.dataPointIndex;
                                    if (dataPointIndex === undefined || dataPointIndex === null) {
                                        return "";
                                    }
                                    var row = rows[dataPointIndex];
                                    var employed = (row && row.employed) || 0;
                                    var total = self.getCampusEmploymentTotal(row);
                                    if (!total) {
                                        return "0 / 0";
                                    }
                                    return employed + " / " + total;
                                },
                            },
                        },
                    },
                },
                stroke: {
                    width: 1,
                    colors: ["#ffffff"],
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: "11px",
                        fontWeight: 600,
                        colors: ["#ffffff", "#6B7280"],
                    },
                    formatter: function (val, opts) {
                        var seriesIndex = opts && opts.seriesIndex;
                        if (seriesIndex === 1) {
                            return "";
                        }
                        var dataPointIndex = opts && opts.dataPointIndex;
                        if (dataPointIndex === undefined || dataPointIndex === null) {
                            return "";
                        }
                        var row = rows[dataPointIndex];
                        var employed = (row && row.employed) || 0;
                        var total = self.getCampusEmploymentTotal(row);
                        if (!total || !employed) {
                            return "";
                        }
                        return Math.round((employed / total) * 100) + "%";
                    },
                },
                xaxis: {
                    categories: categories,
                    max: this.getCampusEmploymentChartMaxY(rows),
                    tickAmount: 4,
                    labels: {
                        hideOverlappingLabels: true,
                        trim: false,
                        formatter: function (val) {
                            return Math.round(val);
                        },
                    },
                },
                legend: {
                    position: "bottom",
                    fontSize: "13px",
                    markers: {
                        width: 10,
                        height: 10,
                        radius: 3,
                    },
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                    followCursor: true,
                    offsetX: 20,
                    offsetY: 12,
                    custom: function (opts) {
                        var dataPointIndex =
                            opts && opts.dataPointIndex !== undefined && opts.dataPointIndex !== null
                                ? opts.dataPointIndex
                                : null;
                        if (dataPointIndex === null || !rows[dataPointIndex]) {
                            return "";
                        }
                        var row = rows[dataPointIndex];
                        var program = row.specialization || "";
                        var employed = row.employed || 0;
                        var notEmployed = self.getCampusEmploymentNotEmployed(row);
                        var total = self.getCampusEmploymentTotal(row);
                        var pct = total > 0 ? Math.round((employed / total) * 100) : 0;
                        return (
                            '<div style="padding:10px 12px;font-size:12px;line-height:1.45;text-align:left;pointer-events:none;">' +
                            '<div style="font-weight:700;color:#111827;margin-bottom:6px;">' +
                            program +
                            "</div>" +
                            '<div style="color:#166534;">Employed: <strong>' +
                            employed +
                            "</strong></div>" +
                            '<div style="color:#6B7280;">Not employed: <strong>' +
                            notEmployed +
                            "</strong></div>" +
                            '<div style="margin-top:6px;padding-top:6px;border-top:1px solid #E5E7EB;color:#111827;">' +
                            "Total Graduates: <strong>" +
                            total +
                            "</strong></div>" +
                            '<div style="color:#111827;">' +
                            "Rate: <strong>" +
                            pct +
                            "%</strong> (" +
                            employed +
                            " ÷ " +
                            total +
                            ")" +
                            "</div>" +
                            "</div>"
                        );
                    },
                },
            };
        },
        buildCampusEmploymentChartOptions(rows) {
            var categories = (rows || []).map(function (result) {
                return result.campus_code;
            });
            var barHeight = "58%";
            if (rows && rows.length <= 2) {
                barHeight = "48%";
            } else if (rows && rows.length <= 4) {
                barHeight = "42%";
            } else if (rows && rows.length <= 6) {
                barHeight = "50%";
            }
            var colors = this.getCampusEmploymentChartColors();
            var self = this;
            return {
                chart: {
                    type: "bar",
                    stacked: true,
                    stackType: "normal",
                    toolbar: { show: false },
                    animations: {
                        enabled: false,
                    },
                },
                colors: colors,
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: barHeight,
                        borderRadius: 2,
                        dataLabels: {
                            total: {
                                enabled: true,
                                offsetX: 8,
                                style: {
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    color: "#1F2937",
                                },
                                formatter: function (val, opts) {
                                    var dataPointIndex = opts && opts.dataPointIndex;
                                    if (dataPointIndex === undefined || dataPointIndex === null) {
                                        return "";
                                    }
                                    var row = rows[dataPointIndex];
                                    var employed = (row && row.employed) || 0;
                                    var total = self.getCampusEmploymentTotal(row);
                                    if (!total) {
                                        return "0 / 0";
                                    }
                                    return employed + " / " + total;
                                },
                            },
                        },
                    },
                },
                stroke: {
                    width: 1,
                    colors: ["#ffffff"],
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: "11px",
                        fontWeight: 600,
                        colors: ["#ffffff", "#6B7280"],
                    },
                    formatter: function (val, opts) {
                        var seriesIndex = opts && opts.seriesIndex;
                        if (seriesIndex === 1) {
                            return "";
                        }
                        var dataPointIndex = opts && opts.dataPointIndex;
                        if (dataPointIndex === undefined || dataPointIndex === null) {
                            return "";
                        }
                        var row = rows[dataPointIndex];
                        var employed = (row && row.employed) || 0;
                        var total = self.getCampusEmploymentTotal(row);
                        if (!total || !employed) {
                            return "";
                        }
                        return Math.round((employed / total) * 100) + "%";
                    },
                },
                xaxis: {
                    categories: categories,
                    max: this.getCampusEmploymentChartMaxY(rows),
                    tickAmount: 4,
                    labels: {
                        hideOverlappingLabels: true,
                        trim: false,
                        formatter: function (val) {
                            return Math.round(val);
                        },
                    },
                },
                legend: {
                    position: "bottom",
                    fontSize: "13px",
                    markers: {
                        width: 10,
                        height: 10,
                        radius: 3,
                    },
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                    followCursor: true,
                    offsetX: 20,
                    offsetY: 12,
                    custom: function (opts) {
                        var dataPointIndex =
                            opts && opts.dataPointIndex !== undefined && opts.dataPointIndex !== null
                                ? opts.dataPointIndex
                                : null;
                        if (dataPointIndex === null || !rows[dataPointIndex]) {
                            return "";
                        }
                        var row = rows[dataPointIndex];
                        var campus = row.campus_code || "";
                        var employed = row.employed || 0;
                        var notEmployed = self.getCampusEmploymentNotEmployed(row);
                        var total = self.getCampusEmploymentTotal(row);
                        var pct = total > 0 ? Math.round((employed / total) * 100) : 0;
                        return (
                            '<div style="padding:10px 12px;font-size:12px;line-height:1.45;text-align:left;pointer-events:none;">' +
                            '<div style="font-weight:700;color:#111827;margin-bottom:6px;">' +
                            campus +
                            "</div>" +
                            '<div style="color:#166534;">Employed: <strong>' +
                            employed +
                            "</strong></div>" +
                            '<div style="color:#6B7280;">Not employed: <strong>' +
                            notEmployed +
                            "</strong></div>" +
                            '<div style="margin-top:6px;padding-top:6px;border-top:1px solid #E5E7EB;color:#111827;">' +
                            "Total: <strong>" +
                            total +
                            "</strong></div>" +
                            '<div style="color:#111827;">' +
                            "Rate: <strong>" +
                            pct +
                            "%</strong> (" +
                            employed +
                            " ÷ " +
                            total +
                            ")" +
                            "</div>" +
                            "</div>"
                        );
                    },
                },
            };
        },
        buildPieDistributionTooltipHtml(headerLabel, seriesValues, labels, segmentColors, highlightIndex) {
            var total = 0;
            var i;
            var highlightedValue = 0;
            var pct = "0.0";
            var html = "";
            if (!seriesValues || !seriesValues.length) {
                return "";
            }
            for (i = 0; i < seriesValues.length; i++) {
                total += seriesValues[i] || 0;
            }
            highlightedValue = seriesValues[highlightIndex] || 0;
            pct = this.formatPieDistributionRate(highlightedValue, total);
            html =
                '<div style="padding:10px 12px;font-size:12px;line-height:1.45;text-align:left;pointer-events:none;background:#ffffff;color:#111827;">' +
                '<div style="font-weight:700;color:#111827;margin-bottom:6px;">' +
                headerLabel +
                "</div>";
            for (i = 0; i < labels.length; i++) {
                var color = "#6B7280";
                if (segmentColors && segmentColors[i]) {
                    color = segmentColors[i];
                }
                html +=
                    '<div style="color:' +
                    color +
                    ';">' +
                    labels[i] +
                    ": <strong>" +
                    (seriesValues[i] || 0) +
                    "</strong></div>";
            }
            html +=
                '<div style="margin-top:6px;padding-top:6px;border-top:1px solid #E5E7EB;color:#111827;">' +
                "Total: <strong>" +
                total +
                "</strong></div>" +
                '<div style="color:#111827;">' +
                "Rate: <strong>" +
                pct +
                "%</strong> (" +
                highlightedValue +
                " ÷ " +
                total +
                ")" +
                "</div>" +
                "</div>";
            return html;
        },
        buildPieDistributionTooltip(labels, series, segmentColors) {
            var self = this;
            return {
                theme: "light",
                fillSeriesColor: false,
                custom: function (opts) {
                    var seriesIndex = null;
                    var seriesValues = series;
                    if (opts && opts.seriesIndex !== undefined && opts.seriesIndex !== null) {
                        seriesIndex = opts.seriesIndex;
                    }
                    if (seriesIndex === null || seriesIndex < 0) {
                        return "";
                    }
                    if (!seriesValues || !seriesValues.length) {
                        if (opts && opts.series && opts.series.length) {
                            seriesValues = opts.series;
                        } else if (
                            opts &&
                            opts.w &&
                            opts.w.globals &&
                            opts.w.globals.series &&
                            opts.w.globals.series.length
                        ) {
                            seriesValues = opts.w.globals.series;
                        } else {
                            return "";
                        }
                    }
                    var headerLabel = labels[seriesIndex] || "";
                    return self.buildPieDistributionTooltipHtml(
                        headerLabel,
                        seriesValues,
                        labels,
                        segmentColors,
                        seriesIndex
                    );
                },
            };
        },
        getCardNumber(card, key) {
            if (!card || card[key] === undefined || card[key] === null) {
                return 0;
            }
            return Number(card[key]) || 0;
        },
        buildEmploymentRateTooltip(card) {
            var employed = this.getCardNumber(card, "employed");
            var graduated = this.getCardNumber(card, "graduated");
            return {
                title: "Employment Rate",
                formula: "Employed ÷ Graduated × 100",
                primaryLabel: "Employed",
                primaryValue: employed,
                secondaryLabel: "Not Employed",
                secondaryValue: graduated - employed,
                totalLabel: "Graduated",
                totalValue: graduated,
                rate: this.formatTooltipRate(employed, graduated),
            };
        },
        buildAdjustedEmploymentRateTooltip(card) {
            var employedEligible = this.getCardNumber(card, "employed_eligible");
            var eligible = this.getCardNumber(card, "eligible_employment");
            return {
                title: "Adjusted Employment Rate",
                formula: "Employed (Eligible) ÷ Eligible for Employment × 100",
                primaryLabel: "Employed (Eligible)",
                primaryValue: employedEligible,
                secondaryLabel: "Eligible Not Employed",
                secondaryValue: eligible - employedEligible,
                totalLabel: "Eligible for Employment",
                totalValue: eligible,
                rate: this.formatTooltipRate(employedEligible, eligible),
            };
        },
        buildWithinMonthRateTooltip(card, item) {
            var label = item && item.label ? item.label : "";
            var countKey = item && item.countKey ? item.countKey : "";
            var employedWithin = this.getCardNumber(card, countKey);
            var eligible = this.getCardNumber(card, "eligible_employment");
            var notCounted = this.getCardNumber(card, "not_counted");
            var denominator = eligible - notCounted;
            if (denominator < 0) {
                denominator = 0;
            }
            return {
                title: "Employment Rate Within " + label,
                formula: "Employed within " + label + " ÷ (Eligible − Unemployed < 9 months) × 100",
                primaryLabel: "Employed within " + label,
                primaryValue: employedWithin,
                secondaryLabel: "Other eligible",
                secondaryValue: denominator - employedWithin,
                totalLabel: "Eligible − Unemployed (< 9 months)",
                totalValue: denominator,
                rate: this.formatTooltipRate(employedWithin, denominator),
            };
        },
        showRateTooltip(event, content) {
            var target = event && event.currentTarget ? event.currentTarget : null;
            if (!target || !content) {
                return;
            }
            var position = this.getTooltipPosition(target, 280, 150);
            this.rateTooltip = {
                visible: true,
                top: position.top,
                left: position.left,
                title: content.title,
                formula: content.formula,
                primaryLabel: content.primaryLabel,
                primaryValue: content.primaryValue,
                secondaryLabel: content.secondaryLabel,
                secondaryValue: content.secondaryValue,
                totalLabel: content.totalLabel,
                totalValue: content.totalValue,
                rate: content.rate,
            };
        },
        hideRateTooltip() {
            this.rateTooltip.visible = false;
        },
        openEmploymentDrilldown(title, fileName, overrides) {
            this.drilldown = {
                visible: true,
                title: title || "Alumni Details",
                fileName: fileName || "Employment_Drilldown",
                overrides: overrides || {},
            };
        },
        closeEmploymentDrilldown() {
            this.drilldown.visible = false;
        },
        getEmploymentDrilldownFromSeriesIndex(seriesIndex) {
            if (seriesIndex === 1) {
                return { label: "Not Employed", is_employed_values: ["0", "nil"] };
            }
            return { label: "Employed", is_employed_values: ["1"] };
        },
        mapGenderLabelToCode(label) {
            if (label === "Male" || label === "M") {
                return "M";
            }
            if (label === "Female" || label === "F") {
                return "F";
            }
            return "";
        },
        mapEmploymentStatusLabelToValues(label) {
            if (label === "Employed") {
                return ["1"];
            }
            if (label === "Unemployed") {
                return ["0"];
            }
            if (label === "Not Contacted") {
                return ["nil"];
            }
            return null;
        },
        mapGotJobWithinToList(value) {
            if (!value) {
                return null;
            }
            var options = [
                "Before graduation",
                "Within 3 months",
                "Within 6 months",
                "Within 9 months",
                "Within 12 months",
            ];
            if (value === "After 1 year") {
                return ["After 1 year"];
            }
            var index = options.indexOf(value);
            if (index === -1) {
                return [value];
            }
            return options.slice(0, index + 1);
        },
        mapJobRelatedLabelToFilter(label) {
            if (label === "Yes") {
                return "1";
            }
            if (label === "No") {
                return "0";
            }
            if (label === "Not Updated") {
                return "nil";
            }
            return null;
        },
        getChartSelectionIndex(config) {
            if (
                config &&
                config.dataPointIndex !== undefined &&
                config.dataPointIndex !== null &&
                config.dataPointIndex >= 0
            ) {
                return config.dataPointIndex;
            }
            if (
                config &&
                config.seriesIndex !== undefined &&
                config.seriesIndex !== null &&
                config.seriesIndex >= 0
            ) {
                return config.seriesIndex;
            }
            return null;
        },
        onEntityChartSelect(event, chartContext, config) {
            var dataPointIndex =
                config && config.dataPointIndex !== undefined && config.dataPointIndex !== null
                    ? config.dataPointIndex
                    : null;
            var seriesIndex =
                config && config.seriesIndex !== undefined && config.seriesIndex !== null
                    ? config.seriesIndex
                    : null;
            if (dataPointIndex === null || dataPointIndex < 0) {
                return;
            }
            var categories =
                this.barChartOptions &&
                    this.barChartOptions.xaxis &&
                    this.barChartOptions.xaxis.categories
                    ? this.barChartOptions.xaxis.categories
                    : [];
            var entity = categories[dataPointIndex];
            if (!entity) {
                return;
            }
            var overrides = { entity: entity };
            var statusLabel = "Graduates";
            if (seriesIndex === 1) {
                overrides.is_employed = null;
                overrides.is_employed_values = ["1"];
                statusLabel = "Employed";
            }
            this.openEmploymentDrilldown(
                "Entity - " + entity + " - " + statusLabel,
                "Employment_Entity_" + entity + "_" + statusLabel,
                overrides
            );
        },
        onEmploymentStatusChartSelect(event, chartContext, config) {
            var index = this.getChartSelectionIndex(config);
            if (index === null) {
                return;
            }
            var labels = this.employmentStatusLabels && this.employmentStatusLabels.length
                ? this.employmentStatusLabels
                : this.pieChartOptions && this.pieChartOptions.labels
                    ? this.pieChartOptions.labels
                    : [];
            var label = labels[index];
            if (!label) {
                return;
            }
            var values = this.mapEmploymentStatusLabelToValues(label);
            if (!values) {
                return;
            }
            this.openEmploymentDrilldown(
                "Employment Status - " + label,
                "Employment_Status_" + label,
                {
                    is_employed: null,
                    is_employed_values: values,
                }
            );
        },
        onGraduatesByGenderChartSelect(event, chartContext, config) {
            var index = this.getChartSelectionIndex(config);
            if (index === null) {
                return;
            }
            var labels =
                this.graduatedDonutChartOptions && this.graduatedDonutChartOptions.labels
                    ? this.graduatedDonutChartOptions.labels
                    : [];
            var label = labels[index];
            if (!label) {
                return;
            }
            var genderCode = this.mapGenderLabelToCode(label);
            if (!genderCode) {
                return;
            }
            this.openEmploymentDrilldown(
                "Graduates by Gender - " + label,
                "Employment_Graduates_Gender_" + label,
                { gender: genderCode }
            );
        },
        onEmployedByGenderChartSelect(event, chartContext, config) {
            var index = this.getChartSelectionIndex(config);
            if (index === null) {
                return;
            }
            var labels =
                this.employedDonutChartOptions && this.employedDonutChartOptions.labels
                    ? this.employedDonutChartOptions.labels
                    : [];
            var label = labels[index];
            if (!label) {
                return;
            }
            var genderCode = this.mapGenderLabelToCode(label);
            if (!genderCode) {
                return;
            }
            this.openEmploymentDrilldown(
                "Employed by Gender - " + label,
                "Employment_Employed_Gender_" + label,
                {
                    gender: genderCode,
                    is_employed: null,
                    is_employed_values: ["1"],
                }
            );
        },
        onCampusChartSelect(event, chartContext, config) {
            var dataPointIndex =
                config && config.dataPointIndex !== undefined && config.dataPointIndex !== null
                    ? config.dataPointIndex
                    : null;
            var seriesIndex =
                config && config.seriesIndex !== undefined && config.seriesIndex !== null
                    ? config.seriesIndex
                    : null;
            if (dataPointIndex === null || dataPointIndex < 0) {
                return;
            }
            var row = this.graduatedVsEmployedPerCampus && this.graduatedVsEmployedPerCampus[dataPointIndex]
                ? this.graduatedVsEmployedPerCampus[dataPointIndex]
                : null;
            if (!row || !row.campus_code) {
                return;
            }
            var drill = this.getEmploymentDrilldownFromSeriesIndex(seriesIndex);
            this.openEmploymentDrilldown(
                "Campus - " + row.campus_code + " - " + drill.label,
                "Employment_Campus_" + row.campus_code + "_" + drill.label,
                {
                    campuses: [row.campus_code],
                    is_employed: null,
                    is_employed_values: drill.is_employed_values,
                }
            );
        },
        onJobRelatedToMajorChartSelect(event, chartContext, config) {
            var index = this.getChartSelectionIndex(config);
            if (index === null) {
                return;
            }
            var labels =
                this.jobRelatedToMajorChartOptions && this.jobRelatedToMajorChartOptions.labels
                    ? this.jobRelatedToMajorChartOptions.labels
                    : [];
            var label = labels[index];
            if (!label) {
                return;
            }
            var filterValue = this.mapJobRelatedLabelToFilter(label);
            if (filterValue === null) {
                return;
            }
            this.openEmploymentDrilldown(
                "Job Related to Major - " + label,
                "Employment_Job_Related_" + label,
                { is_job_rel_major: filterValue }
            );
        },
        onEmploymentSourceChartSelect(event, chartContext, config) {
            var index = this.getChartSelectionIndex(config);
            if (index === null) {
                return;
            }
            var item = this.employmentSourceData && this.employmentSourceData[index]
                ? this.employmentSourceData[index]
                : null;
            if (!item || !item.code) {
                return;
            }
            this.openEmploymentDrilldown(
                "Employment Source - " + item.label,
                "Employment_Source_" + item.label,
                { employment_source: [item.code] }
            );
        },
        onEmploymentRateYearChartSelect(event, chartContext, config) {
            var dataPointIndex =
                config && config.dataPointIndex !== undefined && config.dataPointIndex !== null
                    ? config.dataPointIndex
                    : null;
            var seriesIndex =
                config && config.seriesIndex !== undefined && config.seriesIndex !== null
                    ? config.seriesIndex
                    : null;
            if (dataPointIndex === null || dataPointIndex < 0) {
                return;
            }
            var row = this.employmentRatePerAcademicYear && this.employmentRatePerAcademicYear[dataPointIndex]
                ? this.employmentRatePerAcademicYear[dataPointIndex]
                : null;
            if (!row || !row.academic_year) {
                return;
            }
            var year = row.academic_year;
            if (seriesIndex === 1) {
                this.openEmploymentDrilldown(
                    "Adjusted Employment Rate - " + year,
                    "Employment_Adjusted_Rate_" + year,
                    {
                        academic_years: [year],
                        eligible_for_employment: 1,
                    }
                );
                return;
            }
            this.openEmploymentDrilldown(
                "Employment Rate - " + year,
                "Employment_Rate_" + year,
                { academic_years: [year] }
            );
        },
        onProgramChartSelect(event, chartContext, config) {
            var dataPointIndex =
                config && config.dataPointIndex !== undefined && config.dataPointIndex !== null
                    ? config.dataPointIndex
                    : null;
            var seriesIndex =
                config && config.seriesIndex !== undefined && config.seriesIndex !== null
                    ? config.seriesIndex
                    : null;
            if (dataPointIndex === null || dataPointIndex < 0) {
                return;
            }
            var row = this.employmentRatePerProgram && this.employmentRatePerProgram[dataPointIndex]
                ? this.employmentRatePerProgram[dataPointIndex]
                : null;
            if (!row || !row.specialization) {
                return;
            }
            var drill = this.getEmploymentDrilldownFromSeriesIndex(seriesIndex);
            this.openEmploymentDrilldown(
                "Program - " + row.specialization + " - " + drill.label,
                "Employment_Program_" + row.specialization + "_" + drill.label,
                {
                    specialization: row.specialization,
                    is_employed: null,
                    is_employed_values: drill.is_employed_values,
                }
            );
        },
        onGotJobWithinChartSelect(event, chartContext, config) {
            var index = this.getChartSelectionIndex(config);
            if (index === null) {
                return;
            }
            var categories =
                this.gotJobWithinChartOptions &&
                    this.gotJobWithinChartOptions.xaxis &&
                    this.gotJobWithinChartOptions.xaxis.categories
                    ? this.gotJobWithinChartOptions.xaxis.categories
                    : [];
            var category = categories[index];
            if (!category) {
                return;
            }
            this.openEmploymentDrilldown(
                "Got Job Within - " + category,
                "Employment_Got_Job_Within_" + category,
                {
                    got_job_within: category,
                    got_job_within_list: null,
                }
            );
        },
        onUnemployedReasonChartSelect(event, chartContext, config) {
            var index = this.getChartSelectionIndex(config);
            if (index === null) {
                return;
            }
            var categories =
                this.unemployedReasonChartOption &&
                    this.unemployedReasonChartOption.xaxis &&
                    this.unemployedReasonChartOption.xaxis.categories
                    ? this.unemployedReasonChartOption.xaxis.categories
                    : [];
            var category = categories[index];
            if (!category) {
                return;
            }
            this.openEmploymentDrilldown(
                "Unemployed Reason - " + category,
                "Employment_Unemployed_Reason_" + category,
                { unemployed_reason: category }
            );
        },
        onTopEmployerClick(employerName) {
            if (!employerName || employerName === "Other Employers") {
                return;
            }
            this.openEmploymentDrilldown(
                "Employer - " + employerName,
                "Employment_Employer_" + employerName,
                {
                    employer: employerName,
                    is_employed_values: ["1"],
                }
            );
        },
        getTooltipPosition(target, tooltipWidth, tooltipHeight) {
            var rect = target.getBoundingClientRect();
            var gap = 8;
            var left = rect.left + rect.width / 2 - tooltipWidth / 2;
            var top = rect.bottom + gap;
            var viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            var viewportHeight = window.innerHeight || document.documentElement.clientHeight;

            if (left < 8) {
                left = 8;
            }
            if (left + tooltipWidth > viewportWidth - 8) {
                left = viewportWidth - tooltipWidth - 8;
            }
            if (top + tooltipHeight > viewportHeight - 8) {
                top = rect.top - tooltipHeight - gap;
            }
            if (top < 8) {
                top = 8;
            }

            return {
                top: top,
                left: left,
            };
        },
        getUniqueWithinMonthRates(card) {
            if (!card) {
                return [];
            }
            var intervals = [
                { label: '3 months', rate: card.within_3_months_adjustable_employment_rate, countKey: 'within_3_months_cumulative' },
                { label: '6 months', rate: card.within_6_months_adjustable_employment_rate, countKey: 'within_6_months_cumulative' },
                { label: '9 months', rate: card.within_9_months_adjustable_employment_rate, countKey: 'within_9_months_cumulative' },
                { label: '12 months', rate: card.within_12_months_adjustable_employment_rate, countKey: 'within_12_months_cumulative' },
            ];
            var result = [];
            for (var i = 0; i < intervals.length; i++) {
                var current = intervals[i];
                if (i > 0 && Number(current.rate) === Number(intervals[i - 1].rate)) {
                    continue;
                }
                result.push(current);
            }
            return result;
        },
        async getCampusList() {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetCampusList($student_status: String, $entity: String) {
                            getCampusList(student_status: $student_status, entity: $entity) {
                                entity
                                campus_code
                            }
                        }
                    `,
                    variables: {
                        student_status: "IG",
                        entity: "",
                    },
                    fetchPolicy: "network-only",
                });
                this.campusList = data.getCampusList;
            } catch (error) {
                console.log(error);
            }
        },
        async getAcademicYears() {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetAcademicYearsWithEntity($student_status: String, $entity: String) {
                            getAcademicYearsWithEntity(student_status: $student_status, entity: $entity) {
                                entity
                                academic_year
                            }
                        }
                    `,
                    variables: {
                        student_status: "IG",
                        entity: "",
                    },
                    fetchPolicy: "network-only",
                });
                this.academicYearsList = data.getAcademicYearsWithEntity;
            } catch (error) {
                console.log(error);
            }
        },
        collapseDropDown(dropDownName = '') {
            if (dropDownName) {
                if (dropDownName != 'campus') {
                    this.showCampusDropDown = false;
                }
                if (dropDownName != 'academic_year') {
                    this.showAcademicYearDropDown = false;
                }
            } else {
                this.showAcademicYearDropDown = false;
                this.showCampusDropDown = false;
            }
        },
        downloadReport() {
            this.isLoading = true;
            const element = this.$refs.screenshotArea;
            html2canvas(element).then(canvas => {
                const screenshotUrl = canvas.toDataURL('image/png');
                saveAs(screenshotUrl, "EMPLOYMENT_REPORT.png");
                this.isLoading = false;
            }).catch(error => {
                console.error('Error capturing screenshot:', error);
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(-100%);
}

.perspective {
    perspective: 1000px;
}

.transform-style-preserve-3d {
    transform-style: preserve-3d;
}

.backface-hidden {
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
}

.rotate-y-180 {
    transform: rotateY(180deg);
}

.ht-389 {
    height: 389px;
}

.rate-badge {
    cursor: help;
}

.header-info-trigger {
    cursor: help;
}

.header-info-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 9999px;
    border: 1px solid #94a3b8;
    color: #64748b;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    text-transform: none;
}

.rate-tooltip-fixed {
    position: fixed;
    z-index: 9999;
    min-width: 240px;
    max-width: 320px;
    padding: 10px 12px;
    font-size: 12px;
    line-height: 1.45;
    text-align: left;
    pointer-events: none;
    background: #ffffff;
    color: #111827;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(15, 23, 42, 0.12);
    white-space: normal;
}

.rate-tooltip__title {
    font-weight: 700;
    color: #111827;
    margin-bottom: 4px;
}

.rate-tooltip__formula {
    margin-bottom: 8px;
    color: #475569;
    font-size: 11px;
}

.rate-tooltip__line--primary {
    color: #144685;
}

.rate-tooltip__line--muted {
    color: #64748b;
}

.rate-tooltip__divider {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid #e5e7eb;
    color: #111827;
}

.rate-tooltip__rate {
    color: #111827;
}

@responsive {
    .text-xxs {
        font-size: 8px;
    }

    .text-sm {
        font-size: 14px;
    }
}
</style>
